import React, { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useEndpoints } from '../../hooks/useEndpoints';
import { IAlert } from '../../models/alert';
import { IBodyMapAlert, BodyMapAlertType } from '../../models/bodyMapAlert';
import styles from './AlertsDropdown.module.scss';

function formatRelative(dateStr: string | undefined | null): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m fa`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h fa`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'ieri';
  if (days < 7) return `${days}gg fa`;
  return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

function buildAlertLink(alert: IAlert): string | null {
  if (alert.athleteId) return `/dashboard/users/${alert.athleteId}`;
  return null;
}

const BODY_MAP_ALERT_LABELS: Record<BodyMapAlertType, string> = {
  high_intensity: 'Intensit\u00e0 alta',
  multiple_new_zones: 'Zone nuove multiple',
  persistence: 'Fastidio persistente',
  doms_cap: 'DOMS cap raggiunto',
};

interface AlertsDropdownProps {
  onClose?: () => void;
}

export const AlertsDropdown: React.FC<AlertsDropdownProps> = ({ onClose }) => {
  const endpoints = useEndpoints();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: alertsData, isLoading: alertsLoading } = useQuery({
    queryKey: ['alerts'],
    queryFn: () => endpoints.getAlerts({ limit: 30 }).then((r) => r.data),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });

  const allAlerts: IAlert[] = (alertsData as any)?.alerts ?? (Array.isArray(alertsData) ? alertsData as IAlert[] : []);
  const BODY_MAP_ALERT_TYPES = new Set(['complaint_intense', 'complaint_persistent', 'complaint_prolonged', 'complaint_recurrence', 'team_complaint_trend']);
  const alerts = allAlerts.filter((a) => !BODY_MAP_ALERT_TYPES.has(a.type) && !a.read);

  const markReadMutation = useMutation({
    mutationFn: (id: string) => endpoints.patchAlertRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alerts', 'unread-count'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => endpoints.patchAlertsReadAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alerts', 'unread-count'] });
    },
  });

  const { mutate: markRead } = markReadMutation;

  const handleAlertClick = useCallback(
    (alert: IAlert) => {
      if (!alert.read) markRead(alert._id);
      const link = buildAlertLink(alert);
      if (link) {
        navigate(link);
        onClose?.();
      }
    },
    [markRead, navigate, onClose]
  );

  const unreadCount = alerts.length;

  const { data: bodyMapAlertsData, isLoading: bodyMapLoading } = useQuery({
    queryKey: ['body-map-alerts'],
    queryFn: () =>
      endpoints.getBodyMapAlerts({ acknowledged: 'false', limit: 30 }).then((r) => r.data),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });

  const bodyMapAlerts: IBodyMapAlert[] = Array.isArray(bodyMapAlertsData) ? bodyMapAlertsData : [];

  const acknowledgeMutation = useMutation({
    mutationFn: (id: string) => endpoints.postBodyMapAlertAcknowledge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['body-map-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['body-map-alerts', 'unread-count'] });
    },
  });

  const handleBodyMapAlertClick = useCallback(
    (alert: IBodyMapAlert) => {
      navigate(`/dashboard/users/${alert.athleteId}?tab=fastidi`);
      onClose?.();
    },
    [navigate, onClose]
  );

  const handleOpenInjury = useCallback(
    (e: React.MouseEvent, alert: IBodyMapAlert) => {
      e.stopPropagation();
      const injuryDate = alert.type === 'persistence'
        ? new Date(new Date(alert.triggeredAt).getTime() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        : new Date(alert.triggeredAt).toISOString().split('T')[0];
      const description = [
        `${BODY_MAP_ALERT_LABELS[alert.type]}`,
        alert.zone ? `zona: ${alert.zone.replace(/_/g, ' ')}` : null,
        alert.payload?.intensity ? `intensit\u00e0: ${alert.payload.intensity}` : null,
      ].filter(Boolean).join(', ');
      navigate(
        `/dashboard/users/${alert.athleteId}?tab=storico-clinico`,
        {
          state: {
            prefillInjury: {
              bodyZone: alert.zone,
              injuryDate,
              description,
              sourceBodyMapComplaintId: alert.bodyMapComplaintId,
            },
          },
        },
      );
      onClose?.();
    },
    [navigate, onClose]
  );

  const handleAcknowledge = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      acknowledgeMutation.mutate(id);
    },
    [acknowledgeMutation]
  );

  const isLoading = alertsLoading || bodyMapLoading;
  const totalCount = unreadCount + bodyMapAlerts.length;

  return (
    <div className={styles.dropdown}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <span>Recovery</span>
          {unreadCount > 0 && <span className={styles.headerCount}>{unreadCount}</span>}
        </div>
        {unreadCount > 0 && (
          <button
            className={styles.markAllBtn}
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
          >
            Segna tutti letti
          </button>
        )}
      </div>

      {isLoading && <div className={styles.empty}>Caricamento...</div>}

      {!isLoading && totalCount === 0 && (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>{'\u2713'}</div>
          Nessun alert attivo
        </div>
      )}

      {alerts.map((alert) => (
        <div
          key={alert._id}
          className={`${styles.alertItem} ${!alert.read ? styles.unread : ''}`}
          onClick={() => handleAlertClick(alert)}
        >
          <div
            className={`${styles.dot} ${
              alert.severity === 'info'
                ? styles.dotInfo
                : alert.severity === 'warning'
                ? styles.dotWarning
                : styles.dotCritical
            }`}
          />
          <div className={styles.alertContent}>
            <div className={styles.alertMessage}>{alert.message}</div>
            <div className={styles.alertTime}>{formatRelative(alert.createdAt)}</div>
          </div>
          {!alert.read && (
            <button
              className={styles.ackBtn}
              onClick={(e) => {
                e.stopPropagation();
                markRead(alert._id);
              }}
              disabled={markReadMutation.isPending}
            >
              Segna letto
            </button>
          )}
        </div>
      ))}

      {!isLoading && bodyMapAlerts.length > 0 && (
        <>
          <div className={styles.sectionHeader}>
            <span>Fastidi fisici</span>
            <span className={styles.sectionCount}>{bodyMapAlerts.length}</span>
          </div>

          {bodyMapAlerts.map((alert) => (
            <div
              key={alert._id}
              className={`${styles.bodyMapAlertRow} ${!alert.acknowledgedAt ? styles.unread : ''}`}
              onClick={() => handleBodyMapAlertClick(alert)}
            >
              <div
                className={`${styles.dot} ${alert.severity === 'high' ? styles.dotHigh : styles.dotMedium}`}
              />
              <div className={styles.alertContent}>
                <div className={styles.alertMessage}>
                  {alert.athleteName ? `${alert.athleteName}: ` : ''}
                  {BODY_MAP_ALERT_LABELS[alert.type]}
                  {alert.zone ? ` \u2014 ${alert.zone.replace(/_/g, ' ')}` : ''}
                </div>
                <div className={styles.alertTime}>
                  {formatRelative(alert.triggeredAt)}
                </div>
              </div>
              <div className={styles.actions}>
                {!alert.acknowledgedAt && (
                  <button
                    className={styles.ackBtn}
                    onClick={(e) => handleAcknowledge(e, alert._id)}
                    disabled={acknowledgeMutation.isPending}
                  >
                    Segna letto
                  </button>
                )}
                {alert.suggestsInjury && (
                  <button
                    className={styles.injuryBtn}
                    onClick={(e) => handleOpenInjury(e, alert)}
                  >
                    Apri infortunio
                  </button>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default AlertsDropdown;
